import { formatSecondsAsTimer } from "@vericus/cadmus-common";
import { Alert, colors, Pill } from "@vericus/cadmus-ui";

import { LabelledPill } from "./labelled-pill";
import * as styles from "./response-requirements.css";

export interface ResponseRequirementsProps {
  baseResponseTimeSeconds: number | null;
  basePreparationTimeSeconds: number | null;
  studentResponseTimeSeconds: number | null;
  studentPreparationTimeSeconds: number | null;
  baseMaxAttempts: number | null;
  studentMaxAttempts: number | null;
  attemptsRemaining: number;
  /** The reattempt window as it applies to this student, in seconds. */
  reattemptWindowSeconds: number | null;
  hasSubmittedResponse?: boolean;
}

export function ResponseRequirements(props: ResponseRequirementsProps) {
  const {
    baseResponseTimeSeconds,
    basePreparationTimeSeconds,
    studentResponseTimeSeconds,
    studentPreparationTimeSeconds,
    baseMaxAttempts,
    studentMaxAttempts,
    attemptsRemaining,
    reattemptWindowSeconds,
    hasSubmittedResponse = false,
  } = props;

  const responseTimeSeconds =
    studentResponseTimeSeconds ?? baseResponseTimeSeconds;
  const preparationTimeSeconds =
    studentPreparationTimeSeconds ?? basePreparationTimeSeconds;

  const hasLimitedAttempts =
    studentMaxAttempts != null && studentMaxAttempts > 1;

  const hasSpentAttempts =
    studentMaxAttempts != null && attemptsRemaining < studentMaxAttempts;
  const showAttempts =
    studentMaxAttempts != null && (hasLimitedAttempts || hasSpentAttempts);

  const extraAttempts = extraOf(baseMaxAttempts, studentMaxAttempts);
  const extraResponseTimeSeconds = extraOf(
    baseResponseTimeSeconds,
    studentResponseTimeSeconds
  );
  const extraPreparationTimeSeconds = extraOf(
    basePreparationTimeSeconds,
    studentPreparationTimeSeconds
  );

  const reattemptWindowMinutes =
    reattemptWindowSeconds != null
      ? Math.ceil(reattemptWindowSeconds / 60)
      : null;

  // The server sends a window duration even for a question with a single
  // attempt, so `hasLimitedAttempts` is what keeps the notice off that one.
  const showReattemptBanner =
    reattemptWindowMinutes != null &&
    hasLimitedAttempts &&
    !hasSubmittedResponse;

  return (
    <div className={styles.root}>
      <div className={styles.tagRow}>
        <LabelledPill
          label="Response time"
          value={`${formatSecondsAsTimer(responseTimeSeconds ?? 0, true)} mm:ss`}
          extra={
            extraResponseTimeSeconds > 0 ? (
              <Pill
                basic
                lowerCase
              >{`+${formatSecondsAsTimer(extraResponseTimeSeconds, true)} Extra`}</Pill>
            ) : undefined
          }
        />

        {preparationTimeSeconds != null && preparationTimeSeconds > 0 && (
          <LabelledPill
            label="Preparation time"
            value={`${formatSecondsAsTimer(preparationTimeSeconds, true)} mm:ss`}
            extra={
              extraPreparationTimeSeconds > 0 ? (
                <Pill
                  basic
                  lowerCase
                >{`+${formatSecondsAsTimer(extraPreparationTimeSeconds, true)} Extra`}</Pill>
              ) : undefined
            }
          />
        )}

        {showAttempts && (
          <LabelledPill
            label="Attempts remaining"
            value={`${attemptsRemaining} of ${studentMaxAttempts}`}
            extra={
              extraAttempts > 0 ? (
                <Pill basic lowerCase>{`+${extraAttempts} Extra`}</Pill>
              ) : undefined
            }
          />
        )}
      </div>
      {showReattemptBanner && (
        <Alert
          severity="warning"
          size="sm"
          overrides={{ bg: colors.orange100 }}
          className={styles.reattemptAlert}
        >
          Re-attempts will expire{" "}
          <b>
            {reattemptWindowMinutes} minute{plural(reattemptWindowMinutes)}{" "}
            after you stop recording
          </b>
        </Alert>
      )}
    </div>
  );
}

function plural(count: number) {
  return count === 1 ? "" : "s";
}

/** How much more `student` grants than `base`, or 0 if not an increase. */
function extraOf(base: number | null, student: number | null): number {
  return base != null && student != null && student > base ? student - base : 0;
}
